import React from "react";
import { 
  ViewInventoryColumn,
  CharacteristicsColumnCell,
  LadderCharacteristicsCell,
  LadderNameColumnCell,
  SourceColumnCell,
  ListIdColumnCell,
  ListNameColumnCell,
  EnhancedRfqActionsCell,
  AccountPositionActionsCell // NEW
} from "./columnConfig";

// Registry of render functions for special columns
const renderFunctions = {
  // View Inventory column
  viewInventory: {
    characteristic: (cellData, rowData) => (
      <ViewInventoryColumn rowData={rowData} type="characteristic" />
    ),
    ladder: (cellData, rowData) => (
      <ViewInventoryColumn rowData={rowData} type="ladder" />
    ),
    default: (cellData, rowData) => (
      <ViewInventoryColumn rowData={rowData} type="characteristic" />
    ),
  },
  
  // Characteristics columns
  characteristics: {
    characteristic: (cellData, rowData, handleViewJson) => (
      <CharacteristicsColumnCell rowData={rowData} handleViewJson={handleViewJson} />
    ),
    ladder: (cellData, rowData, handleViewJson) => (
      <LadderCharacteristicsCell rowData={rowData} handleViewJson={handleViewJson} />
    ),
    default: (cellData, rowData, handleViewJson) => (
      <CharacteristicsColumnCell rowData={rowData} handleViewJson={handleViewJson} />
    ),
  },
  
  // Ladder name on a bond characteristic row (opens the read-only ladder popup)
  ladderName: (cellData, rowData, handleViewJson) => (
    <LadderNameColumnCell rowData={rowData} handleViewJson={handleViewJson} />
  ),

  // Source column
  source: (cellData, rowData, handleViewJson) => (
    <SourceColumnCell rowData={rowData} handleViewJson={handleViewJson} />
  ),

  // Actions column
  actions: (cellData, rowData) => (
    <EnhancedRfqActionsCell rowData={rowData} />
  ),

  // List ID - the number shared by every bond of one List RFQ, shown as LST-000001, and a way
  // into the list it names. Clicking it opens that whole list read-only; a row with no list
  // renders "---" as plain text. Same component the fresh column set uses, so a trader with
  // saved columns gets the same cell rather than a dead number.
  listId: (cellData, rowData, handleViewJson) => (
    <ListIdColumnCell rowData={rowData} handleViewJson={handleViewJson} />
  ),
  listName: (cellData, rowData, handleViewJson) => (
    <ListNameColumnCell rowData={rowData} handleViewJson={handleViewJson} />
  ),
  // NEW: account positions actions
  action_account_pos: (cellData, rowData) => (
    <AccountPositionActionsCell rowData={rowData} />
  )
};

// Helper to determine proper source type based on section key
const inferSourceFromSectionKey = (sectionKey) => {
  if (!sectionKey) return 'default';
  
  // console.log(`Inferring source from section key: "${sectionKey}"`);
  
  // Tab-specific detection: 1-1 is characteristic, 1-2 is ladder
  if (sectionKey === '1-1' || sectionKey === 'tab-1-1') {
    // console.log('Matched section key 1-1 to characteristic');
    return 'characteristic';
  }
  
  if (sectionKey === '1-2' || sectionKey === 'tab-1-2') {
    // console.log('Matched section key 1-2 to ladder');
    return 'ladder';
  }
  
  // More specific detection logic - explicitly handle common sectionKey formats
  if (sectionKey === 'columns-tab-0-1' || sectionKey === 'characteristic-columns') {
    return 'characteristic';
  }
  
  if (sectionKey === 'columns-tab-0-2' || sectionKey === 'columns-tab-1-2' || sectionKey === 'ladder-columns') {
    return 'ladder';
  }
  
  // More general pattern matching as fallback
  if (sectionKey.includes('characteristic') || sectionKey.includes('-1') && !sectionKey.includes('-2')) {
    return 'characteristic';
  }
  
  if (sectionKey.includes('ladder') || sectionKey.includes('-2')) {
    return 'ladder';
  }
  
  // console.log(`No specific match for section key "${sectionKey}", using default`);
  return 'default';
};

/**
 * Apply render functions to special columns based on the column key and source
 * @param {Array} columns - Array of column objects
 * @param {string} source - Source of the columns (characteristic, ladder, etc.)
 * @param {string} sectionKey - Section key to infer source if not provided
 * @returns {Array} - Array of columns with render functions applied
 */
export const applyRenderFunctions = (columns, source = null, sectionKey = null) => {
  if (!columns || !Array.isArray(columns)) return [];
  
  // Try to infer source from sectionKey if source is not provided
  let effectiveSource = source;
  if (!effectiveSource && sectionKey) {
    effectiveSource = inferSourceFromSectionKey(sectionKey);
    // console.log(`Inferred source '${effectiveSource}' from section key '${sectionKey}'`);
  }

  // If still no source, use default
  if (!effectiveSource) {
    effectiveSource = 'default';
  }

  // Always log which render functions we're applying
  // console.log(`Applying render functions with effectiveSource: ${effectiveSource}, sectionKey: ${sectionKey}, keys: ${columns.map(col => col.key).join(', ')}`);
  
  return columns.map(column => {
    const columnKey = column.key;
    
    // Special case for viewInventory column
    if (columnKey === "viewInventory") {
      // Always use a render function, defaulting if needed
      const renderFn = renderFunctions.viewInventory[effectiveSource] || renderFunctions.viewInventory.default;
      // console.log(`Applied render function to viewInventory column with source: ${effectiveSource}`);
      return {
        ...column,
        render: renderFn
      };
    }
    
    // Special case for characteristics column
    if (columnKey === "characteristics") {
      // Always use a render function, defaulting if needed
      const renderFn = renderFunctions.characteristics[effectiveSource] || renderFunctions.characteristics.default;
      // console.log(`Applied render function to characteristics column with source: ${effectiveSource}`);
      return {
        ...column,
        render: renderFn
      };
    }
    
    // Special case for ladder name column — render is lost when columns are
    // persisted to localStorage, so always re-attach it here.
    if (columnKey === "ladderName") {
      return {
        ...column,
        render: renderFunctions.ladderName
      };
    }

    // Special case for source column
    if (columnKey === "source" && !column.render) {
      if (effectiveSource === "ma-inventory" || sectionKey === "ma-inventory") {
        return {
          ...column,
          render: () => "MA"
        };
      }

      if (renderFunctions.source) {
        return {
          ...column,
          render: renderFunctions.source
        };
      }
    }

    // Special case for actions column
    // Keep existing custom renderer (e.g., MA Inventory Add Order button)
    if (columnKey === "actions" && renderFunctions.actions && !column.render) {
      return {
        ...column,
        render: renderFunctions.actions
      };
    }

    // List ID column. Same reason as ladderName above: the render function does not survive
    // being persisted to localStorage, so without re-attaching it here a saved column set
    // prints the raw integer - "1" where the trader expects LST-000001.
    //
    // Guarded on !column.render so a caller that supplied its own formatter keeps it, matching
    // how actions and source behave.
    if (columnKey === "listId" && renderFunctions.listId && !column.render) {
      return {
        ...column,
        render: renderFunctions.listId
      };
    }

    // List Name, re-attached for a saved column set for the same reason as List ID.
    if (columnKey === "listName" && renderFunctions.listName && !column.render) {
      return {
        ...column,
        render: renderFunctions.listName
      };
    }

    // NEW: account positions actions column
    if (columnKey === "action_account_pos" && renderFunctions.action_account_pos) {
      return {
        ...column,
        render: renderFunctions.action_account_pos
      };
    }
    
    // Return original column if no special rendering needed
    return column;
  });
};

export default renderFunctions;
